import type { RadioOption } from '@/components'
import {
  AbsenceStatus,
  AbsenceStatusCombobox,
  AbsenceTypeCombobox,
  Card,
  DatePicker,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  MonthSelect,
  Radios,
  useHideBasedOnLeaderGroup,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import type { UseLeaderGroupsSuspenseReturn } from '@/utils'
import { PERMISSIONS, useFormWatch, usePermissions } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Activity } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import z from 'zod'

export const AbsencesFiltersSchema = z.object({
  view: z.enum(['table', 'calendar', 'requests']),
  users: z.string().array(),
  absenceType: z.string(),
  requestStatus: z.enum(AbsenceStatus).or(z.literal('')),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  byCreatedDate: z.boolean(),
  month: z.date(),
  leaderGroup: z.string(),
})

export type AbsencesFiltersForm = z.infer<typeof AbsencesFiltersSchema>

interface Props {
  form: UseFormReturn<AbsencesFiltersForm>
  leaderGroups: UseLeaderGroupsSuspenseReturn
}

export function AbsencesFilters({ form, leaderGroups }: Props) {
  const translations = useTranslate({
    users: 'absences.filters.users',
    absenceType: 'absences.filters.absence-type',
    requestStatus: 'absences.filters.request-status',
    dateRange: 'absences.filters.date-range',
    month: 'absences.filters.month',
    leaderGroup: 'absences.filters.leader-group',

    select: 'common.select-ellipsis',

    filtering: 'absences.filters.date-filtering',
    byCreatedDate: 'absences.filters.by-created-date',
    byStartDate: 'absences.filters.by-start-date',
  })

  const view = useFormWatch(form, 'view')
  const leaderGroup = useFormWatch(form, 'leaderGroup')

  const hideUserItem = useHideBasedOnLeaderGroup(leaderGroup, leaderGroups)

  const { hasPermissions } = usePermissions()
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )

  const byCreatedOptions: RadioOption<'created' | 'started'>[] = [
    {
      value: 'started',
      label: translations.byStartDate,
    },
    {
      value: 'created',
      label: translations.byCreatedDate,
    },
  ]

  return (
    <Card className={cn('mb-4 p-0')} contentClassName="p-0">
      <form
        className={cn(
          'grid p-4 gap-4',
          'md:flex md:flex-wrap md:justify-start'
        )}
      >
        <Activity mode={canViewOthersAbsence ? 'visible' : 'hidden'}>
          <Controller
            control={form.control}
            name="users"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.users}</FieldLabel>
                <UserCombobox
                  {...field}
                  className="max-w-[350px]"
                  mode="multiple"
                  hideItem={hideUserItem}
                  placeholder={translations.select}
                />
              </Field>
            )}
          />
        </Activity>

        <Activity
          mode={
            canViewOthersAbsence && leaderGroups.hasAccess ? 'visible' : 'hidden'
          }
        >
          <Controller
            control={form.control}
            name="leaderGroup"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.leaderGroup}</FieldLabel>
                <LeaderGroupCombobox
                  {...field}
                  mode="single"
                  module="Absences"
                  clearable
                  placeholder={translations.select}
                  onChange={value => {
                    field.onChange(value)
                    form.setValue('users', [])
                  }}
                />
              </Field>
            )}
          />
        </Activity>

        <Controller
          control={form.control}
          name="absenceType"
          render={({ field }) => (
            <Field>
              <FieldLabel>{translations.absenceType}</FieldLabel>
              <AbsenceTypeCombobox
                {...field}
                mode="single"
                placeholder={translations.select}
              />
            </Field>
          )}
        />

        <Activity mode={view === 'requests' ? 'hidden' : 'visible'}>
          <Controller
            control={form.control}
            name="requestStatus"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.requestStatus}</FieldLabel>
                <AbsenceStatusCombobox
                  {...field}
                  mode="single"
                  placeholder={translations.select}
                />
              </Field>
            )}
          />
        </Activity>

        <Activity mode={view === 'table' ? 'visible' : 'hidden'}>
          <Controller
            control={form.control}
            name="dateRange"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.dateRange}</FieldLabel>
                <DatePicker
                  {...field}
                  mode="range"
                  format="PP"
                  footer={
                    <div className="flex justify-center gap-4 mt-4">
                      <FieldLabel>{translations.filtering}</FieldLabel>

                      <Controller
                        control={form.control}
                        name="byCreatedDate"
                        render={({ field }) => (
                          <Radios
                            value={field.value ? 'created' : 'started'}
                            options={byCreatedOptions}
                            orientation="horizontal"
                            onChange={value =>
                              field.onChange(value === 'created')
                            }
                          />
                        )}
                      />
                    </div>
                  }
                  onChange={value => {
                    if (value === null) return
                    field.onChange(value)
                  }}
                />
              </Field>
            )}
          />
        </Activity>

        <Activity mode={view === 'calendar' ? 'visible' : 'hidden'}>
          <Controller
            control={form.control}
            name="month"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.month}</FieldLabel>
                <MonthSelect {...field} />
              </Field>
            )}
          />
        </Activity>
      </form>
    </Card>
  )
}
